import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  Method,
  isAxiosError,
} from "axios";
import AuthService from "./auth.service";
import { DEFAULT_HTTP_TIMEOUT } from "../core/constants";
import { HttpResponseError, HttpTimeoutError } from "../core/errors";

abstract class AbstractService {
  private axiosClient;

  constructor(baseURL: string) {
    this.axiosClient = axios.create({
      baseURL,
      timeout: DEFAULT_HTTP_TIMEOUT,
    });

    // Add response interceptor for token refresh (embedded mode)
    this.axiosClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If we get a 401 and we're using external auth, request token refresh
        if (
          error.response?.status === 401 &&
          AuthService.isUsingExternalAuth() &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            // Request new token from parent system and wait for it
            await AuthService.requestTokenRefresh();

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${AuthService.accessToken}`;
            return this.axiosClient.request(originalRequest);
          } catch (refreshError) {
            // If refresh fails, reject with original error
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  protected async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T | null> {
    const res = await this.send<T>("get", url, undefined, config);
    if (this.is2xx(res)) return res.data;
    else if (res.status === 404) return null;
    throw new HttpResponseError(res);
  }

  protected async post<T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.send<T>("post", url, body, config);
    if (this.is2xx(res)) return res.data;
    throw new HttpResponseError(res);
  }

  protected async put<T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.send<T>("put", url, body, config);
    if (this.is2xx(res)) return res.data;
    throw new HttpResponseError(res);
  }

  protected async patch<T>(
    url: string,
    body: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.send<T>("patch", url, body, config);
    if (this.is2xx(res)) return res.data;
    throw new HttpResponseError(res);
  }

  protected async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const res = await this.send<T>("delete", url, undefined, config);
    if (this.is2xx(res)) return res.data;
    throw new HttpResponseError(res);
  }

  protected async send<T>(
    method: Method,
    url: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T, any>> {
    try {
      if (!config) config = {};
      if (!config.headers) config.headers = this.getDefaultHeaders();
      else {
        config.headers = {
          ...this.getDefaultHeaders(),
          ...config.headers,
        };
      }

      const req: AxiosRequestConfig = {
        method,
        url,
        data,
        ...config,
      };
      const res = await this.axiosClient.request(req);

      return res;
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        return err.response;
      }

      const error = err as any;

      // Handle CORS errors - these typically don't have a response
      if (isAxiosError(err) && !err.response) {
        const isCorsError =
          error.message?.includes("CORS") ||
          error.message?.includes("Network Error") ||
          error.code === "ERR_NETWORK" ||
          (error.message && !error.response);

        if (isCorsError) {
          console.error("CORS error detected. This may be due to:", {
            message: error.message,
            code: error.code,
            baseURL: this.axiosClient.defaults.baseURL,
            origin: window.location.origin,
          });
        }
      }

      if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
        throw new HttpTimeoutError(error);
      } else if (!(err instanceof Error)) {
        throw new Error(
          `unexpected error during http request: ${JSON.stringify(err)}`,
          {
            cause: err,
          },
        );
      }
      throw err;
    }
  }

  protected getDefaultHeaders(): any {
    const token = AuthService.accessToken;
    if (!token) {
      console.warn(
        "AbstractService: No access token available. Request may fail.",
      );
    }
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  private is2xx(res: AxiosResponse): boolean {
    return 200 <= res.status && res.status < 300;
  }
}

export default AbstractService;
